const APP_THEMES = [
  'fresh-green',
  'ocean-blue',
  'vibrant-purple',
  'warm-earth',
  'sunset-orange',
] as const;

export type AppTheme = (typeof APP_THEMES)[number];

export const DEFAULT_APP_THEME: AppTheme = 'fresh-green';

const THEME_COLOR_META_SELECTOR = 'meta[name="theme-color"][data-managed-by-theme="true"]';

const THEME_COLORS: Record<AppTheme, { light: string; dark: string }> = {
  'fresh-green': { light: '#38b954', dark: '#1a6536' },
  'ocean-blue': { light: '#0078d7', dark: '#0044a6' },
  'vibrant-purple': { light: '#8b5cf6', dark: '#3d1a5e' },
  'warm-earth': { light: '#a3c47a', dark: '#2e2821' },
  'sunset-orange': { light: '#ff9f0a', dark: '#cc5200' },
};

const isAppTheme = (value: string | null): value is AppTheme =>
  value !== null && APP_THEMES.includes(value as AppTheme);

const getManagedThemeColorMeta = (): HTMLMetaElement | null =>
  document.querySelector<HTMLMetaElement>(THEME_COLOR_META_SELECTOR);

const ensureThemeColorMetaTag = (): HTMLMetaElement => {
  const managedMetaTag = getManagedThemeColorMeta();
  if (managedMetaTag) {
    return managedMetaTag;
  }

  const existingMetaTag = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]:not([media])');
  if (existingMetaTag) {
    existingMetaTag.dataset.managedByTheme = 'true';
    return existingMetaTag;
  }

  const metaTag = document.createElement('meta');
  metaTag.setAttribute('name', 'theme-color');
  metaTag.dataset.managedByTheme = 'true';
  document.head.appendChild(metaTag);
  return metaTag;
};

export const getStoredTheme = (): AppTheme => {
  const storedTheme = localStorage.getItem('appTheme');
  return isAppTheme(storedTheme) ? storedTheme : DEFAULT_APP_THEME;
};

export const getStoredDarkModePreference = (): boolean =>
  localStorage.getItem('appDarkMode') === 'true';

export const getThemeColor = (theme: AppTheme, darkModeEnabled: boolean): string =>
  THEME_COLORS[theme][darkModeEnabled ? 'dark' : 'light'];

export const getCurrentTheme = (): AppTheme => {
  const activeTheme = document.documentElement.getAttribute('data-theme');
  return isAppTheme(activeTheme) ? activeTheme : getStoredTheme();
};

export const updateThemeColorMetaTag = (
  theme: AppTheme = getCurrentTheme(),
  darkModeEnabled: boolean = document.documentElement.classList.contains('dark'),
): void => {
  const metaTag = ensureThemeColorMetaTag();
  metaTag.setAttribute('content', getThemeColor(theme, darkModeEnabled));
};

export const applyThemePreference = (theme: AppTheme): void => {
  localStorage.setItem('appTheme', theme);
  document.documentElement.setAttribute('data-theme', theme);
  updateThemeColorMetaTag(theme);
};

export const applyDarkModePreference = (darkModeEnabled: boolean): void => {
  localStorage.setItem('appDarkMode', String(darkModeEnabled));
  document.documentElement.classList.toggle('dark', darkModeEnabled);
  updateThemeColorMetaTag(getCurrentTheme(), darkModeEnabled);
};

export const applyStoredAppearancePreferences = (): {
  darkModeEnabled: boolean;
  theme: AppTheme;
} => {
  const theme = getStoredTheme();
  const darkModeEnabled = getStoredDarkModePreference();

  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.classList.toggle('dark', darkModeEnabled);
  updateThemeColorMetaTag(theme, darkModeEnabled);

  return {
    theme,
    darkModeEnabled,
  };
};
